import * as R from 'remeda';
import { usePageSpaceContext } from '../../features/annotation-view/document-canvas-svg/page/PageSpaceContext';
import { Vector } from '../../lib/spaceConvertor';
import { MIN_GRID_SIZE } from './constants';
import { useGridContext } from './GridContext';
import { ResizingEdge } from './utils';

// Returns a function that converst a webPx diff to PDF pixels and clamps it
// for each individual direction
export const useResizeClamps = (
  resizingEdge: ResizingEdge
): ((diff: Vector) => Vector) => {
  const { gridState } = useGridContext();

  const { pageWidth, pageHeight } = usePageSpaceContext();

  const firstRowTop = gridState.rows[0]?.topPosition ?? 0;
  const firstColLeft = gridState.columns[0]?.leftPosition ?? 0;

  const topClamp = (dy: number): number =>
    R.clamp(Math.round(dy), {
      min: -firstRowTop,
      max: gridState.height - MIN_GRID_SIZE,
    });

  const rightClamp = (dx: number): number =>
    R.clamp(Math.round(dx), {
      min: -gridState.width + MIN_GRID_SIZE,
      max: pageWidth - gridState.width - firstColLeft,
    });

  const bottomClamp = (dy: number): number =>
    R.clamp(Math.round(dy), {
      min: -gridState.height + MIN_GRID_SIZE,
      max: pageHeight - firstRowTop - gridState.height,
    });

  const leftClamp = (dx: number): number =>
    R.clamp(Math.round(dx), {
      min: -firstColLeft,
      max: gridState.width - MIN_GRID_SIZE,
    });

  const noDeltaClamp = (_d: number) => 0;

  const clampMap: Record<
    ResizingEdge,
    [(x: number) => number, (y: number) => number]
  > = {
    top: [noDeltaClamp, topClamp],
    'top-right': [rightClamp, topClamp],
    right: [rightClamp, noDeltaClamp],
    'bottom-right': [rightClamp, bottomClamp],
    bottom: [noDeltaClamp, bottomClamp],
    'bottom-left': [leftClamp, bottomClamp],
    left: [leftClamp, noDeltaClamp],
    'top-left': [leftClamp, topClamp],
  };

  return (diff: Vector) => [
    clampMap[resizingEdge][0](diff[0]),
    clampMap[resizingEdge][1](diff[1]),
  ];
};
